import { useState, useEffect } from 'react';
import { Card, CardBody, Button, Input, Table, TableHeader, TableBody, TableColumn, TableRow, TableCell, Switch } from '@nextui-org/react';
import { supabase } from 'common/src/supabase';
import { toast } from 'react-hot-toast';

interface Popup {
  id: number;
  title: string;
  image_url: string;
  link: string;
  active: boolean;
}

export default function PopupPage() {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPopups();
  }, []);

  const fetchPopups = async () => {
    const { data, error } = await supabase
      .from('popups')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Error al cargar popups');
      return;
    }
    setPopups(data || []);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let image_url = '';
      
      if (image) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('popups')
          .upload(fileName, image);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('popups')
          .getPublicUrl(fileName);
          
        image_url = publicUrl;
      }

      const { error } = await supabase
        .from('popups')
        .insert([{ title, image_url, link, active: true }]);

      if (error) throw error;

      toast.success('Popup creado exitosamente');
      setTitle('');
      setLink('');
      setImage(null);
      fetchPopups();
    } catch (error) {
      toast.error('Error al crear popup');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (id: number, currentActive: boolean) => {
    const { error } = await supabase
      .from('popups')
      .update({ active: !currentActive })
      .eq('id', id);

    if (error) {
      toast.error('Error al actualizar estado');
      return;
    }

    fetchPopups();
  };

  const handleDelete = async (id: number) => {
    const { error } = await supabase
      .from('popups')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Error al eliminar popup');
      return;
    }

    toast.success('Popup eliminado exitosamente');
    fetchPopups();
  };

  return (
    <Card>
      <CardBody>
        <h1 className="text-2xl font-bold mb-4">Administrar Popups</h1>
        
        <form onSubmit={handleSubmit} className="space-y-4 mb-8">
          <Input
            label="Título"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <Input
            label="Link"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            required
          />
          <Input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            required
          />
          <Button
            color="primary"
            type="submit"
            isLoading={loading}
          >
            Crear Popup
          </Button>
        </form>

        <Table aria-label="Tabla de Popups">
          <TableHeader>
            <TableColumn>TÍTULO</TableColumn>
            <TableColumn>IMAGEN</TableColumn>
            <TableColumn>LINK</TableColumn>
            <TableColumn>ACTIVO</TableColumn>
            <TableColumn>ACCIONES</TableColumn>
          </TableHeader>
          <TableBody>
            {popups.map((popup) => (
              <TableRow key={popup.id}>
                <TableCell>{popup.title}</TableCell>
                <TableCell>
                  <img 
                    src={popup.image_url} 
                    alt={popup.title} 
                    className="w-20 h-20 object-cover"
                  />
                </TableCell>
                <TableCell>{popup.link}</TableCell>
                <TableCell>
                  <Switch
                    isSelected={popup.active}
                    onValueChange={() => toggleActive(popup.id, popup.active)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDelete(popup.id)}
                  >
                    Eliminar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}